<template>
    <Drawer v-model:visible="visible" :header="title" position="right" :class="sizeClass" :dismissable="!busy" :close-on-escape="!busy" @hide="onHide()">
        <div class="flex flex-col h-full">
            <div class="flex-1">
                <!-- built-in simple confirmation -->
                <template v-if="!component">
                    <div class="flex items-start gap-3">
                        <i v-if="icon" :class="[icon, 'text-2xl mt-0.5']" />
                        <p class="text-surface-600 dark:text-surface-300" v-html="message"></p>
                    </div>
                </template>

                <!-- custom component flow -->
                <component :is="component" v-else v-bind="props" ref="componentRef" />
            </div>

            <!-- the outcome of a refused command; the input above stays as it was -->
            <Message v-if="error" severity="error" icon="pi pi-times-circle" class="mt-4">{{ error }}</Message>
            <!-- a command that succeeded in part; the drawer stays open so the user can retry the rest -->
            <Message v-if="warning" severity="warn" icon="pi pi-exclamation-triangle" class="mt-4">{{ warning }}</Message>
        </div>

        <template #footer>
            <div class="flex gap-2 justify-end items-center">
                <!-- a component's own footer content, such as a link, teleports here: left of the buttons -->
                <div id="drawer-footer-start" class="mr-auto" />
                <Button :label="warning ? 'Close' : rejectProps.label" :severity="rejectProps.severity" :outlined="rejectProps.outlined" :text="rejectProps.text" :icon="rejectProps.icon" :disabled="busy" @click="onCancel()" />
                <Button :label="accept.label" :severity="accept.severity" :outlined="accept.outlined" :text="accept.text" :icon="accept.icon" :disabled="!canConfirm" :loading="busy" @click="onConfirm()" />
            </div>
        </template>
    </Drawer>
</template>

<script setup lang="ts">
import { useDrawer } from '@/composables/useDrawer';
import { useFeedback } from '@/composables/useFeedback';
import { drawerState, type DrawerActionProps } from '@/plugins/drawerService';
import { computed, ref } from 'vue';

const drawer = useDrawer();
const feedback = useFeedback();
const { visible, title, message, icon, component, props, sizeClass, acceptProps, rejectProps, busy, error, warning } = drawerState;

const componentRef = ref();

//a component may restyle the accept button for what its input now means, such as a red Delete once
//a delete is chosen, by exposing acceptProps; what it leaves out stays as the caller set it
const accept = computed<DrawerActionProps>(() => ({ ...acceptProps.value, ...componentRef.value?.acceptProps }));

const canConfirm = computed(() => !busy.value && (!componentRef.value || (!componentRef.value.invalid && !componentRef.value.pending)));

const onCancel = function () {
    drawer.cancel();
};

//the mask click and escape close the Drawer on their own; treat them as Cancel
const onHide = function () {
    if (drawerState.onAccept || drawerState.onReject) drawer.cancel();
};

const onConfirm = async function () {
    //a component that only shows something, such as the body of a delete confirmation, exposes no
    //model and nothing to touch
    componentRef.value?.touch?.();

    if (!canConfirm.value) return;

    try {
        // Pass the drawer component's exposed model along, so the accept callback
        // receives the entered data instead of undefined.
        await drawer.confirm(componentRef.value?.model);
    } catch (error) {
        //a refused command stays in the drawer (see drawerService); anything else lands here
        feedback.error('Something went wrong', error);
    }
};
</script>
